import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { AddNewPolicyBodyRequest } from '@/domains/policies/types/api/requests/AddPolicyBodyRequest';
import { EDSFiltersRequestBody } from '@/features/eds/types/api/requests/EDSFiltersRequestBody';
import { EdsSortingRequest } from '@/features/eds/types/api/requests/EdsSortingRequestBody';
import { DeletePolicyBodyRequest } from '../types/api/requests/DeletePolicyBodyRequest';
import { LinkPolicyToClaimRequest } from '../types/api/requests/LinkClaimBodyRequest';
import { PotentialPolicyPaginatedListResponse } from '../types/api/responses/PotentialPolicyPaginatedListResponse';
import type { PolicyClaimOptionPayload, PolicySliceStatusesPayload } from './types/PolicySliceState';
import { policySliceInitialState } from './constants/policySliceConstants';

const policySlice = createSlice({
  name: 'policySlice',
  initialState: policySliceInitialState,
  reducers: {
    getPolicyStatuses: (_state, _payload) => {},
    getCreatePolicyOptionsSuccess: (state, { payload }: PayloadAction<PolicySliceStatusesPayload>) => {
      state.createPolicyOptions = payload;
    },
    addNewPolicy: (state, _action: PayloadAction<AddNewPolicyBodyRequest>) => {
      state.statuses.isCreatePotentialPolicyLoading = true;
    },
    addNewPolicySuccess: (state) => {
      state.statuses.isCreatePotentialPolicyLoading = false;
    },
    linkPolicyToClaim: (_, _action: PayloadAction<LinkPolicyToClaimRequest>) => {},
    getClaimOptions: (_, _action: PayloadAction<string>) => {},
    getClaimOptionsSuccess: (state, { payload }: PayloadAction<PolicyClaimOptionPayload[]>) => {
      state.claimOptions = payload;
    },
    setClaimOptionsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.statuses.isClaimOptionsLoading = payload;
    },
    deletePolicy: (_, _action: PayloadAction<DeletePolicyBodyRequest>) => {},
    getPotentialPoliciesFilteredList: (state) => {
      state.data.pagination = policySliceInitialState.data.pagination;
    },
    setPotentialPoliciesListLoadingStatus: (state, { payload }: PayloadAction<boolean>) => {
      state.statuses.isGetPotentialPolicyLoading = payload;
    },
    getPotentialPoliciesListSuccess: (
      state,
      {
        payload: { shouldRewrite = true, data },
      }: PayloadAction<{ data: PotentialPolicyPaginatedListResponse; shouldRewrite?: boolean }>,
    ) => {
      if (shouldRewrite) {
        state.data.listings = [...data.listings];
        state.data.pagination = data.pagination;
      } else {
        state.data.listings = [...state.data.listings, ...data.listings];
        state.data.pagination = data.pagination;
      }
    },
    incrementPotentialPoliciesListPage: (state) => {
      state.data.pagination.page++;
    },
    updatePotentialPoliciesSavedFiltersForSending: (state, { payload }: PayloadAction<EDSFiltersRequestBody>) => {
      state.savedFiltersForSending = payload;
    },
    updatePotentialPoliciesSavedSortingForSending: (state, { payload }: PayloadAction<EdsSortingRequest>) => {
      state.savedSortingForSending = payload;
    },
    resetPotentialPoliciesSlice: () => policySliceInitialState,
  },
});

export const {
  getPolicyStatuses,
  getCreatePolicyOptionsSuccess,
  addNewPolicy,
  addNewPolicySuccess,
  linkPolicyToClaim,
  getClaimOptions,
  getClaimOptionsSuccess,
  setClaimOptionsLoading,
  deletePolicy,
  getPotentialPoliciesFilteredList,
  incrementPotentialPoliciesListPage,
  getPotentialPoliciesListSuccess,
  setPotentialPoliciesListLoadingStatus,
  updatePotentialPoliciesSavedFiltersForSending,
  updatePotentialPoliciesSavedSortingForSending,
  resetPotentialPoliciesSlice,
} = policySlice.actions;

export const policySliceReducerPath = policySlice.reducerPath;
export const policySliceReducer = policySlice.reducer;
